import type { ReactNode } from 'react';
import { Checkbox, Money, Text } from '@belong/ui';
import type { ConsumerFailedPaymentModel } from './failed-payments.types';
import { formatShortDate, getBalanceUniqueIds } from './failed-payments-utils';
import { FAILED_PAYMENTS_STRINGS } from './failed-payments-strings';

type FailedPaymentBalanceSelectionProps = {
  title?: string;
  failedPayments: ConsumerFailedPaymentModel[];
  selectedBalanceUniqueIds: string[];
  trailingRow?: ReactNode;
  onToggleBalanceIds: (balanceUniqueIds: string[]) => void;
};

export function FailedPaymentBalanceSelection({
  title,
  failedPayments,
  selectedBalanceUniqueIds,
  trailingRow,
  onToggleBalanceIds,
}: FailedPaymentBalanceSelectionProps) {
  return (
    <div className="flex flex-col gap-sm">
      {title && (
        <Text variant="h3" fontWeight="semibold">
          {title}
        </Text>
      )}

      <div className="flex flex-col gap-md">
        {failedPayments.map((failedPayment) => {
          const balanceIds = getBalanceUniqueIds(failedPayment);
          const balanceUniqueId = balanceIds[0];
          if (!balanceUniqueId) return null;

          const isSelected = balanceIds.some((id) =>
            selectedBalanceUniqueIds.includes(id),
          );
          const failedDate = formatShortDate(failedPayment.failedOn);

          return (
            <div
              key={balanceUniqueId}
              role="button"
              aria-pressed={isSelected}
              tabIndex={0}
              onClick={() => onToggleBalanceIds(balanceIds)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onToggleBalanceIds(balanceIds);
                }
              }}
              className="flex w-full cursor-pointer items-center justify-between text-left"
            >
              <span className="flex min-w-0 flex-1 flex-col gap-2xs">
                <span
                  role="presentation"
                  onClick={(event) => event.stopPropagation()}
                >
                  <Checkbox
                    id={`failed-payment-balance-${balanceUniqueId}`}
                    checked={isSelected}
                    label={failedPayment.label ?? ''}
                    onChange={() => onToggleBalanceIds(balanceIds)}
                    labelTextClassName="truncate"
                  />
                </span>
                <Text
                  variant="p2"
                  fontWeight="semibold"
                  className="pl-[28px] text-red"
                >
                  {failedDate
                    ? `${FAILED_PAYMENTS_STRINGS['item.failed_prefix']} ${failedDate}`
                    : FAILED_PAYMENTS_STRINGS['modal.entry.badge']}
                </Text>
              </span>
              <Money
                value={-Math.abs(failedPayment.amount)}
                format="DOLLARS_WITH_CENTS"
                className="ml-sm shrink-0 text-body font-semibold"
              />
            </div>
          );
        })}
        {trailingRow}
      </div>
    </div>
  );
}
